import { AIAgent } from "../types";
import { skincareKnowledgeData } from "./skincare-knowledge";
import { makeupKnowledgeData } from "./makeup-knowledge";
import { fragranceKnowledgeData } from "./fragrance-knowledge";
import { customerServiceData } from "./customer-service";
import { skinScienceIngredientsData } from "./skin-science-ingredients";
import { beautyTrendsTechniquesData } from "./beauty-trends-techniques";
import { safetyData } from "./safety";

export const topics = [
  {
    id: "58f2c720-6ae3-4162-83f2-f2b8d56249ba",
    name: "Customer Personalization",
    description:
      "AI agent's ability to personalize interactions based on customer data and preferences",
    testing: {
      coverage: {
        total: 85.2,
        functional: 92.1,
        edgeCases: 78.5,
        integration: 88.3,
      },
      testMetrics: {
        totalTests: 45,
        passedTests: 38,
        failedTests: 7,
        averageScore: 84.4,
        lastTestRun: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
      },
      performance: {
        averageResponseTime: 1.8,
        accuracy: 87.3,
        successRate: 91.2,
        errorRate: 2.1,
      },
      riskAssessment: {
        riskLevel: "medium",
        criticalIssues: 2,
        highPriorityIssues: 3,
        mediumPriorityIssues: 5,
        lowPriorityIssues: 8,
      },
      testCategories: [
        {
          name: "Data Privacy",
          tests: 12,
          passed: 11,
          coverage: 91.7,
        },
        {
          name: "Personalization Accuracy",
          tests: 18,
          passed: 15,
          coverage: 83.3,
        },
        {
          name: "Customer Segmentation",
          tests: 15,
          passed: 12,
          coverage: 80.0,
        },
      ],
      lastUpdated: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
    },
  },
  {
    id: "9d0cdc6b-b05a-46a0-b819-445ff1d094ab",
    name: "Conversion & Sales",
    description:
      "AI agent's effectiveness in driving sales and converting leads to customers",
    testing: {
      coverage: {
        total: 78.9,
        functional: 85.6,
        edgeCases: 72.3,
        integration: 82.1,
      },
      testMetrics: {
        totalTests: 52,
        passedTests: 41,
        failedTests: 11,
        averageScore: 78.8,
        lastTestRun: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
      },
      performance: {
        averageResponseTime: 2.3,
        accuracy: 82.1,
        successRate: 85.7,
        errorRate: 3.8,
      },
      riskAssessment: {
        riskLevel: "high",
        criticalIssues: 4,
        highPriorityIssues: 6,
        mediumPriorityIssues: 8,
        lowPriorityIssues: 12,
      },
      testCategories: [
        {
          name: "Sales Pipeline",
          tests: 20,
          passed: 16,
          coverage: 80.0,
        },
        {
          name: "Lead Qualification",
          tests: 15,
          passed: 12,
          coverage: 80.0,
        },
        {
          name: "Conversion Optimization",
          tests: 17,
          passed: 13,
          coverage: 76.5,
        },
      ],
      lastUpdated: new Date(Date.now() - 3 * 60 * 60 * 1000), // 3 hours ago
    },
  },
  {
    id: "4fe5d3d9-391e-41fe-b3c0-0bd9f90b4a35",
    name: "Product Vocabulary",
    description:
      "AI agent's knowledge and understanding of product terminology and descriptions",
    testing: {
      coverage: {
        total: 91.5,
        functional: 94.2,
        edgeCases: 88.7,
        integration: 93.1,
      },
      testMetrics: {
        totalTests: 38,
        passedTests: 35,
        failedTests: 3,
        averageScore: 92.1,
        lastTestRun: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
      },
      performance: {
        averageResponseTime: 1.5,
        accuracy: 94.8,
        successRate: 96.3,
        errorRate: 1.2,
      },
      riskAssessment: {
        riskLevel: "low",
        criticalIssues: 0,
        highPriorityIssues: 1,
        mediumPriorityIssues: 2,
        lowPriorityIssues: 4,
      },
      testCategories: [
        {
          name: "Product Knowledge",
          tests: 15,
          passed: 14,
          coverage: 93.3,
        },
        {
          name: "Terminology Accuracy",
          tests: 12,
          passed: 11,
          coverage: 91.7,
        },
        {
          name: "Feature Descriptions",
          tests: 11,
          passed: 10,
          coverage: 90.9,
        },
      ],
      lastUpdated: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
    },
  },
];

// Sample agent data - in real app this would come from API
export const sampleAgent: AIAgent = {
  id: "802b68a1-ae1c-424a-8e8a-dca38f2164b1",
  name: "Beauty Sales Agent",
  description: "AI agent for luxury cosmetics sales and customer consultation",
  status: "active",
  type: "Sales AI",
  performance: {
    accuracy: 85.2,
    responseTime: 2.1,
    successRate: 78.5,
    totalRequests: 8920,
    apiPerformance: {
      latencyOverTime: [
        {
          timestamp: new Date(Date.now() - 60 * 60 * 1000),
          latency: 2.1,
          requestCount: 150,
        },
        {
          timestamp: new Date(Date.now() - 30 * 60 * 1000),
          latency: 1.8,
          requestCount: 200,
        },
      ],
      latencyByRequestCount: [
        {
          requestCount: 100,
          averageLatency: 1.9,
          p95Latency: 2.5,
          p99Latency: 3.2,
        },
        {
          requestCount: 200,
          averageLatency: 2.1,
          p95Latency: 2.8,
          p99Latency: 3.5,
        },
      ],
      successRateByHour: [
        {
          hour: 9,
          successRate: 98.5,
          totalRequests: 120,
          failedRequests: 2,
        },
        {
          hour: 10,
          successRate: 97.2,
          totalRequests: 180,
          failedRequests: 5,
        },
      ],
      errorBreakdown: {
        timeoutErrors: 12,
        rateLimitErrors: 3,
        authenticationErrors: 1,
        serverErrors: 8,
        networkErrors: 5,
        totalErrors: 29,
      },
      throughputMetrics: {
        requestsPerMinute: 15,
        requestsPerHour: 900,
        peakRequestsPerMinute: 25,
        averageConcurrentRequests: 8,
      },
    },
  },
  lastActive: new Date(Date.now() - 2 * 60 * 1000), // 2 minutes ago
  version: "v2.1.3",
  model: "GPT-4",
  tags: ["sales", "luxury", "cosmetics", "customer-service", "nlp"],
  category: "conversational",
  config: {
    endpoint: "https://api.openai.com/v1/chat/completions",
    apiKey: "sk-...",
    timeout: 30000,
    maxTokens: 4000,
    temperature: 0.7,
    modelVersion: "gpt-4-turbo-preview",
    features: [
      "conversation",
      "product-recommendations",
      "customer-personalization",
      "luxury-sales",
      "multi-language",
    ],
  },
  metrics: {
    dailyRequests: 850,
    averageResponseTime: 2.1,
    errorRate: 1.2,
    uptime: 99.5,
    costPerRequest: 0.003,
    totalCost: 25.5,
  },
  testResults: {
    totalTests: 156,
    passedTests: 132,
    failedTests: 24,
    lastTestRun: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
    averageScore: 84.6,
  },
  testRuns: [
    {
      id: "a24d2e4e-6184-4c5c-9a07-76ddd939f843",
      name: "Daily Functional Test Run",
      status: "passed",
      startTime: new Date(Date.now() - 6 * 60 * 60 * 1000),
      endTime: new Date(Date.now() - 6 * 60 * 60 * 1000 + 15 * 60 * 1000),
      duration: 15 * 60,
      totalTests: 156,
      passedTests: 132,
      failedTests: 24,
      score: 84.6,
      triggeredBy: "scheduled",
      environment: "production",
    },
  ],
};

export const visualizationData = {
  id: "2e125d99-2796-43b0-a047-5f6f580e768a",
  name: "Lancôme AI Agent",

  testCoverage: 100,

  children: [
    skincareKnowledgeData,
    makeupKnowledgeData,
    fragranceKnowledgeData,
    customerServiceData,
    skinScienceIngredientsData,
    beautyTrendsTechniquesData,
    safetyData,
  ],
};
